import * as Phaser from 'phaser';
import { PaintedScene } from '../PaintedScene';
import {
  installSnowTexture,
  installBreathTexture,
  createSnowfall,
  createBreathMist,
} from './frostmereAmbient';
import {
  preloadVariantLayers,
  buildVariantSwitcher,
  readFrostmereRestoration,
  getRestorationTier,
  type VariantSwitcher,
  type RestorationTier,
  FROSTMERE_RESTORATION_KEY,
} from './frostmereRestoration';

export const FROZEN_FALLS_PHASER_KEY = 'FrozenFallsScene' as const;
export const FROZEN_FALLS_MANIFEST_KEY = 's-305-frozen-falls' as const;

const DRIP_TEXTURE_KEY = '__frozen_falls_drip__';
const WATERFALL_SCROLL_SPEED = 40;

const VARIANT_CONFIG = {
  tiers: [0, 50, 100] as RestorationTier[],
  layerPaths: {
    0: {
      'sky': 'assets/scenes/frostmere/S-305/layer-sky.png',
      'far-bg': 'assets/scenes/frostmere/S-305/layer-far.png',
      'mid': 'assets/scenes/frostmere/S-305/layer-mid.png',
      'fg': 'assets/scenes/frostmere/S-305/layer-fg.png',
    },
    50: {
      'sky': 'assets/scenes/frostmere/S-305-50pct/layer-sky.png',
      'far-bg': 'assets/scenes/frostmere/S-305-50pct/layer-far.png',
      'mid': 'assets/scenes/frostmere/S-305-50pct/layer-mid.png',
      'fg': 'assets/scenes/frostmere/S-305-50pct/layer-fg.png',
    },
    100: {
      'sky': 'assets/scenes/frostmere/S-305-100pct/layer-sky.png',
      'far-bg': 'assets/scenes/frostmere/S-305-100pct/layer-far.png',
      'mid': 'assets/scenes/frostmere/S-305-100pct/layer-mid.png',
      'fg': 'assets/scenes/frostmere/S-305-100pct/layer-fg.png',
    },
  },
};

export class FrozenFallsScene extends PaintedScene {
  private snowEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private breathEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private dripEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private variantSwitcher: VariantSwitcher | null = null;
  private waterfallTileSprite: Phaser.GameObjects.TileSprite | null = null;

  constructor() {
    super(FROZEN_FALLS_PHASER_KEY, FROZEN_FALLS_MANIFEST_KEY);
  }

  preload(): void {
    super.preload();
    installSnowTexture(this);
    installBreathTexture(this);
    this.installDripTexture();
    preloadVariantLayers(this, 's-305', VARIANT_CONFIG);
  }

  protected override onSceneReady(): void {
    this.variantSwitcher = buildVariantSwitcher(this, 's-305', VARIANT_CONFIG);
    this.snowEmitter = createSnowfall(this, 0.7);

    if (this.walker) {
      this.breathEmitter = createBreathMist(this, this.walker);
    }

    const pct = readFrostmereRestoration(this.game);
    const tier = getRestorationTier(pct);
    if (tier >= 50) {
      this.buildDripEffect();
    }
    if (tier >= 100) {
      this.buildWaterfallScroll();
    }

    this.game.registry.events.on(
      `changedata-${FROSTMERE_RESTORATION_KEY}`,
      this.onRestorationChange,
      this,
    );
  }

  update(time: number, delta: number): void {
    super.update(time, delta);
    if (this.waterfallTileSprite) {
      this.waterfallTileSprite.tilePositionY -= (WATERFALL_SCROLL_SPEED * delta) / 1000;
    }
  }

  protected override onSceneShutdown(): void {
    this.snowEmitter?.destroy();
    this.breathEmitter?.destroy();
    this.dripEmitter?.destroy();
    this.variantSwitcher?.destroy();
    this.waterfallTileSprite?.destroy();
    this.game.registry.events.off(
      `changedata-${FROSTMERE_RESTORATION_KEY}`,
      this.onRestorationChange,
      this,
    );
    this.snowEmitter = null;
    this.breathEmitter = null;
    this.dripEmitter = null;
    this.variantSwitcher = null;
    this.waterfallTileSprite = null;
  }

  private onRestorationChange = (_parent: unknown, value: number): void => {
    const tier = getRestorationTier(value);
    if (tier >= 50 && !this.dripEmitter) {
      this.buildDripEffect();
    }
    if (tier >= 100 && !this.waterfallTileSprite) {
      this.buildWaterfallScroll();
    }
  };

  private installDripTexture(): void {
    if (this.textures.exists(DRIP_TEXTURE_KEY)) return;
    const gfx = this.make.graphics({ x: 0, y: 0 }, false);
    gfx.fillStyle(0x90c8e0, 0.8);
    gfx.fillRect(0, 0, 2, 6);
    gfx.generateTexture(DRIP_TEXTURE_KEY, 2, 6);
    gfx.destroy();
  }

  private buildDripEffect(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    this.dripEmitter = this.add.particles(0, 0, DRIP_TEXTURE_KEY, {
      x: { min: w * 0.55, max: w * 0.75 },
      y: h * 0.3,
      speedY: { min: 60, max: 120 },
      speedX: { min: -3, max: 3 },
      alpha: { start: 0.7, end: 0 },
      scale: { min: 0.8, max: 1.5 },
      lifespan: { min: 1500, max: 2500 },
      frequency: 300,
      quantity: 1,
      maxAliveParticles: 12,
    });
    this.dripEmitter.setScrollFactor(0);
    this.dripEmitter.setDepth(4);
  }

  private buildWaterfallScroll(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    const fallX = w * 0.65;
    const fallY = h * 0.25;
    const fallW = w * 0.12;
    const fallH = h * 0.45;

    const gfx = this.make.graphics({ x: 0, y: 0 }, false);
    for (let y = 0; y < 64; y++) {
      const alpha = 0.15 + Math.sin(y * 0.3) * 0.05;
      gfx.fillStyle(0xb0d8f0, alpha);
      gfx.fillRect(0, y, 32, 1);
    }
    const texKey = '__frozen_falls_water_tile__';
    if (!this.textures.exists(texKey)) {
      gfx.generateTexture(texKey, 32, 64);
    }
    gfx.destroy();

    this.waterfallTileSprite = this.add.tileSprite(fallX, fallY, fallW, fallH, texKey);
    this.waterfallTileSprite.setOrigin(0.5, 0);
    this.waterfallTileSprite.setDepth(2.5);
    this.waterfallTileSprite.setAlpha(0);
    this.waterfallTileSprite.setScrollFactor(0);
    this.waterfallTileSprite.setBlendMode(Phaser.BlendModes.ADD);

    this.tweens.add({
      targets: this.waterfallTileSprite,
      alpha: 0.6,
      duration: 2000,
      ease: 'Sine.easeIn',
    });
  }
}
